/* eslint-disable @typescript-eslint/no-explicit-any */
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import React from 'react';
import { HtmlOutput } from '../../HtmlOutput';
import { callConfigFunction, createHandlebarsInstance, escapeHtml } from '../../WidgetForm/Utils';
import { WidgetHandler } from '../../WidgetHandler';
/**
 * Renders a template output component based on the provided template configuration.
 *
 * Depending on the type of the `template` in `props`, this component delegates
 * rendering to either:
 * - `HandlebarsTemplateOutput` for Handlebars templates.
 * - `JavaScriptTemplateOutput` for JavaScript-based templates.
 *
 * @param {TemplateOutputProps} props - The component props.
 * @param {TemplateConfig} props.template - The template configuration object.
 * @param {WidgetProps<any, any>} props.props - The widget props to be used for rendering the template.
 * @param {WidgetDynamicRow} [props.row] - Optional dynamic row context for rendering row-based templates.
 * @param {boolean} [props.editMode] - Optional flag indicating if the template is being rendered in edit mode.
 * @param {Record<string, CustomTag>} [props.htmlOutputCustomTags] - Optional mapping of custom HTML tags for rendering.
 * @param {any} [props.htmlOutputCustomData] - Optional additional data for rendering custom HTML tags.
 * @returns {React.ReactElement | null} A rendered template output element or `null` if no matching renderer is found.
 */
export const TemplateOutput = (props) => {
    if (isHandlebarsProps(props)) {
        return React.createElement(HandlebarsTemplateOutput, Object.assign({}, props));
    }
    else if (isJavaScriptProps(props)) {
        return React.createElement(JavaScriptTemplateOutput, Object.assign({}, props));
    }
    else {
        return null;
    }
};
function isHandlebarsProps(props) {
    return typeof props.template === 'object' && 'content' in props.template && !!props.template.content;
}
function isJavaScriptProps(props) {
    return typeof props.template === 'object' && 'render' in props.template && !!props.template.render;
}
const HandlebarsTemplateOutput = (props) => {
    var _a, _b, _c;
    const [handlebarsFunction, handlebarsError] = getHandlebarsFunction(props);
    const { data, error } = getHandlebarsData(props);
    let html;
    try {
        if (handlebarsFunction != null && data != null) {
            html = handlebarsFunction(data);
        }
        else if (handlebarsError != null) {
            html = '<div class="alert-error">' + escapeHtml(handlebarsError) + '</div>';
        }
        else if (error) {
            html = '<div class="alert-error">' + escapeHtml(error) + '</div>';
        }
        else {
            html = '<div class="alert-error">Failed to initialize handlebars templating engine</div>';
        }
    }
    catch (error) {
        html = '<div class="alert-error">' + escapeHtml(getErrorMessage(error)) + '</div>';
    }
    return (React.createElement(HtmlOutput, { html: html, customTags: (_a = props.props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.htmlOutputCustomTags, data: (_b = props.props.rootParams.globalWidgetConfig) === null || _b === void 0 ? void 0 : _b.htmlOutputData, customIcons: (_c = props.props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.htmlOutputIcons }));
};
function getHandlebarsInstance(props) {
    const internalData = props.props.rootWidgetProps.internalData;
    let handlebarsInstance = internalData.handlebarsInstance;
    if (handlebarsInstance == null) {
        handlebarsInstance = createHandlebarsInstance();
        internalData.handlebarsInstance = handlebarsInstance;
    }
    return handlebarsInstance;
}
function getHandlebarsFunction(props) {
    const internalData = props.props.internalData;
    let handlebarsTemplate = internalData.handlebarsTemplate;
    if (handlebarsTemplate == null || handlebarsTemplate !== props.template.content) {
        handlebarsTemplate = props.template.content;
        internalData.handlebarsTemplate = handlebarsTemplate;
        try {
            internalData.handlebarsFunction = getHandlebarsInstance(props).compile(handlebarsTemplate);
            delete internalData.handlebarsError;
        }
        catch (error) {
            internalData.handlebarsError = getErrorMessage(error);
        }
    }
    return [internalData.handlebarsFunction, internalData.handlebarsError];
}
function getHandlebarsData(props) {
    var _a, _b, _c;
    if (props.props.editMode || props.editMode) {
        const data = Object.assign({}, (_a = props.template.sample) === null || _a === void 0 ? void 0 : _a.data);
        if (((_b = props.template.sample) === null || _b === void 0 ? void 0 : _b.values) != null) {
            data._internal_values_ = (_c = props.template.sample) === null || _c === void 0 ? void 0 : _c.values;
        }
        return { data: transformHandlebarsDataIfNeeded(props, data) };
    }
    else {
        let handler = new WidgetHandler(props.props, props.row);
        if (props.template.data) {
            handler = handler.select(props.template.data);
        }
        if (handler) {
            return { data: Object.assign(Object.assign({}, transformHandlebarsDataIfNeeded(props, handler.getData(), handler)), { _internal_handler_: handler }) };
        }
        else {
            return { error: 'Component ' + props.template.data + ' was not found' };
        }
    }
}
function transformHandlebarsDataIfNeeded(props, data, handler) {
    if (props.template.dataTransform) {
        return callConfigFunction(props.template.dataTransform, 'template dataTransform', 'value, handler', data, handler);
    }
    else {
        return data;
    }
}
const JavaScriptTemplateOutput = (props) => {
    var _a, _b, _c, _d;
    let handler = new WidgetHandler(props.props);
    if (props.template.data) {
        handler = handler.select(props.template.data);
    }
    let html;
    if (handler) {
        html = callConfigFunction((_a = props.template.render) !== null && _a !== void 0 ? _a : '', 'template render', 'handler', handler);
    }
    else {
        html = '<div class="alert-error">Component ' + props.template.data + ' that was defined in template data was not found</div>';
    }
    return (React.createElement(HtmlOutput, { html: html, customTags: (_b = props.props.rootParams.globalWidgetConfig) === null || _b === void 0 ? void 0 : _b.htmlOutputCustomTags, data: (_c = props.props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.htmlOutputData, customIcons: (_d = props.props.rootParams.globalWidgetConfig) === null || _d === void 0 ? void 0 : _d.htmlOutputIcons }));
};
